import Phaser from 'phaser';
import { GAME_WIDTH, GAME_HEIGHT } from '@/config/gameConfig';
import { THEME } from '@/config/theme';
import { drawArcadeBackground } from '@/utils/arcadeBackground';
import { createButton } from '@/ui/buttons';
import type { DailySharePayload } from '@/utils/dailyShare';
import { formatDailyDisplayDate } from '@/utils/dailyDate';

interface DailyShareSceneData {
  payload: DailySharePayload | null;
  error?: string;
}

export class DailyShareScene extends Phaser.Scene {
  constructor() {
    super({ key: 'DailyShareScene' });
  }

  create(data: DailyShareSceneData): void {
    this.cameras.main.fadeIn(300, 5, 7, 22);
    drawArcadeBackground(this);

    if (!data.payload) {
      this.add.text(GAME_WIDTH / 2, 180, data.error ?? 'Share not available.', {
        fontFamily: THEME.fonts.pixel,
        fontSize: THEME.sizes.bodyLarge,
        color: THEME.colors.textMuted,
      }).setOrigin(0.5);

      createButton(this, {
        x: GAME_WIDTH / 2,
        y: GAME_HEIGHT / 2 + 120,
        text: 'MAIN MENU',
        style: 'primary',
        buttonType: 'normal',
        onClick: () => {
          if (typeof window !== 'undefined') {
            window.history.replaceState({}, '', '/');
          }
          this.scene.start('MenuScene');
        },
      });
      return;
    }

    const name = data.payload.name?.trim();
    const nameText = name || 'Anonymous';

    const card = this.add.rectangle(GAME_WIDTH / 2, 220, 560, 190, THEME.colors.backgroundLightHex, 0.7);
    card.setStrokeStyle(2, THEME.colors.backgroundPanelHex);
    card.setDepth(-1);

    this.add.text(GAME_WIDTH / 2, 160, `${data.payload.score.toLocaleString()} PTS`, {
      fontFamily: THEME.fonts.pixelBold,
      fontSize: THEME.sizes.score,
      color: THEME.colors.yellow,
      stroke: THEME.stroke.color,
      strokeThickness: THEME.stroke.thickness,
    }).setOrigin(0.5);

    this.add.text(GAME_WIDTH / 2, 220, formatDailyDisplayDate(data.payload.date), {
      fontFamily: THEME.fonts.pixelBold,
      fontSize: THEME.sizes.bodyLarge,
      color: THEME.colors.text,
    }).setOrigin(0.5);

    this.add.text(GAME_WIDTH / 2, 265, nameText, {
      fontFamily: THEME.fonts.pixel,
      fontSize: THEME.sizes.bodyLarge,
      color: THEME.colors.textMuted,
    }).setOrigin(0.5);

    this.add.text(GAME_WIDTH / 2, GAME_HEIGHT / 2 + 120, 'Play today\'s daily challenge', {
      fontFamily: THEME.fonts.body,
      fontSize: THEME.sizes.body,
      color: THEME.colors.text,
    }).setOrigin(0.5);

    createButton(this, {
      x: GAME_WIDTH / 2 - 120,
      y: GAME_HEIGHT / 2 + 180,
      text: 'PLAY TODAY',
      style: 'primary',
      buttonType: 'normal',
      onClick: () => {
        if (typeof window !== 'undefined') {
          window.history.replaceState({}, '', '/');
        }
        this.scene.start('DailyChallengeScene');
      },
      sizes: {
        primary: { width: 200, height: 50, fontSize: THEME.sizes.bodyLarge },
      },
    });

    createButton(this, {
      x: GAME_WIDTH / 2 + 120,
      y: GAME_HEIGHT / 2 + 180,
      text: 'MAIN MENU',
      style: 'secondary',
      buttonType: 'normal',
      onClick: () => {
        if (typeof window !== 'undefined') {
          window.history.replaceState({}, '', '/');
        }
        this.scene.start('MenuScene');
      },
      sizes: {
        secondary: { width: 180, height: 44, fontSize: THEME.sizes.body },
      },
    });
  }
}
